import React, { useState, useEffect } from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  colors,
  Divider,
  FormControlLabel,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { listClients, Client } from 'api/clientsApi';

interface Props {
  label: string;
  onChange: (data: Client[]) => void;
  defaultSelected?: Client[];
}

const MultiClientAutoCompleteForFilter = ({
  label,
  onChange,
  defaultSelected,
}: Props) => {
  const { data: clients, isFetching } = useQuery(['list-clients'], listClients);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Client[]>([]);

  useEffect(() => {
    if (defaultSelected && defaultSelected.length > 0 && clients) {
      const defaultSelectedIds = new Set(
        defaultSelected.map((client) => client.id)
      );
      const selectedData = clients.filter((client) =>
        defaultSelectedIds.has(client.id)
      );
      setSelected(selectedData);
    } else {
      setSelected([]);
    }
  }, [defaultSelected, clients]);

  return (
    <>
      <Autocomplete
        multiple
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={isFetching ? [] : clients}
        loading={open && isFetching}
        getOptionLabel={(option) => option?.legalName || ''}
        value={selected}
        renderOption={(option) => (
          <span>
            <Box display="block">
              <Typography variant="body1">{option.legalName}</Typography>
            </Box>
          </span>
        )}
        renderTags={(value: Client[], getTagProps) =>
          value.map((option: Client, index: number) => (
            <Chip
              sx={{ marginRight: '0.4rem' }}
              variant="outlined"
              label={option.legalName}
              {...getTagProps({ index })}
            />
          ))
        }
        getOptionSelected={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {open && isFetching && (
                    <CircularProgress color="inherit" size={20} />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        onChange={(_, data: Client[]) => {
          onChange && onChange(data.map((u) => u));
        }}
        PaperComponent={(paperProps) => {
          const { children, ...restPaperProps } = paperProps;
          return (
            <Paper {...restPaperProps}>
              {clients?.length > 0 && (
                <Box onMouseDown={(e) => e.preventDefault()} pl={1.5} py={0.5}>
                  <FormControlLabel
                    onClick={(e) => {
                      e.preventDefault();
                      onChange && onChange(clients?.map((u) => u));
                      setOpen(false);
                    }}
                    label=""
                    control={
                      <Box m={1}>
                        <Button
                          id="select-all-checkbox"
                          variant="outlined"
                          size="small"
                          sx={{
                            color: colors.grey[600],
                            borderColor: colors.grey[600],
                          }}>
                          Select all
                        </Button>
                      </Box>
                    }
                  />
                </Box>
              )}
              <Divider />
              {children}
            </Paper>
          );
        }}
      />
    </>
  );
};

export default MultiClientAutoCompleteForFilter;
